/**
 * App Store categories: labels and colors (spec 2026-09-30-app-store-categories §4.2).
 *
 * The ids are owned by the API (api/src/registry/appStoreVocabulary.ts), which
 * validates them. appStoreVocabulary.test.ts fails if this list drifts from the API's.
 * Array order is display order, for the chips and the admin dropdown.
 *
 * `bar` colors the tile's top bar, its monogram border and the chip dot. `text` and
 * `textDark` color the category label and monogram letters in light and dark mode.
 * They are darker or lighter shades of `bar`, chosen for at least 4.5:1 contrast,
 * which the test enforces.
 */
export type AppCategoryId = 'insights' | 'pipeline' | 'strategy' | 'search';

export interface CategoryColors {
  bar: string;
  text: string;
  textDark: string;
}

export interface AppCategory extends CategoryColors {
  id: AppCategoryId;
  label: string;
}

export const APP_CATEGORIES: readonly AppCategory[] = [
  { id: 'insights', label: 'Insights & reporting', bar: '#00a3e0', text: '#00739f', textDark: '#4fc3ee' },
  { id: 'pipeline', label: 'Formations & contracting', bar: '#3a7a72', text: '#2f655e', textDark: '#6fb3aa' },
  { id: 'strategy', label: 'Strategy & planning', bar: '#e8a317', text: '#8a5a00', textDark: '#f0bd4f' },
  { id: 'search', label: 'Engagement & activation', bar: '#647c4b', text: '#4f6339', textDark: '#9db67f' },
];

/** Deloitte Green: tiles with no category, or one this build doesn't know. */
export const UNCATEGORIZED_COLORS: CategoryColors = { bar: '#86bc25', text: '#4c7210', textDark: '#a8d64b' };

/** The category for an id, or undefined for a missing or unknown one (treated as none). */
export function categoryById(id: string | undefined): AppCategory | undefined {
  return APP_CATEGORIES.find((c) => c.id === id);
}

/**
 * App Store lifecycle stages (ADR 0043): how finished an app is, stated on every
 * tile beside the category. The ids are owned by the API, like the categories, and
 * the same test keeps the two lists in sync. Array order is least to most finished,
 * which is also the admin dropdown's order.
 *
 * `dot` colors the status dot. `text` and `textDark` color the label in light and
 * dark mode, at least 4.5:1 (the test enforces it). The shades reuse the category
 * and brand palettes, so the tile gains no new colors.
 */
export type AppLifecycleId = 'demo' | 'in-development' | 'beta' | 'live';

export interface AppLifecycle {
  id: AppLifecycleId;
  label: string;
  dot: string;
  text: string;
  textDark: string;
}

export const APP_LIFECYCLES: readonly AppLifecycle[] = [
  { id: 'demo', label: 'Demo', dot: '#8a939d', text: '#4d5560', textDark: '#b6bec8' },
  { id: 'in-development', label: 'In dev', dot: '#e8a317', text: '#8a5a00', textDark: '#f0bd4f' },
  { id: 'beta', label: 'Beta', dot: '#00a3e0', text: '#00739f', textDark: '#4fc3ee' },
  { id: 'live', label: 'Live', dot: '#86bc25', text: '#4c7210', textDark: '#a8d64b' },
];

/** The stage for an id, or undefined for a missing or unknown one (treated as unset). */
export function lifecycleById(id: string | undefined): AppLifecycle | undefined {
  return APP_LIFECYCLES.find((s) => s.id === id);
}
